import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, BadgeCheck, CheckCircle2, ChevronLeft, ChevronRight, Clock, MapPin, Phone, Mail, Tag,
} from 'lucide-react';
import { api, mediaUrl } from '@/lib/api';
import { useVenue } from '@/lib/venue';
import { Button } from '@/components/ui/Button';
import { StarRating } from '@/components/ui/StarRating';
import { amenityInfo } from '@/content/amenities';

// Venue details — shown when a customer picks a venue on /app/venues,
// before booking: photos, rating and recent reviews, about, courts & sports
// (derived server-side from the venue's courts), the facilities & amenities
// the venue admin listed, and running offers. "Book a court" / "See plans" then selects the venue.
const formatHour = (h) => {
  const period = h >= 12 && h < 24 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh} ${period}`;
};

const offerValue = (o) => (o.discount_type === 'percent' ? `${o.discount_value}% off` : `₹${o.discount_value} off`);
const offerScope = { booking: 'on bookings', membership: 'on memberships', both: '' };

const SectionTitle = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-3">{children}</div>
);

export default function VenueDetails() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectVenue } = useVenue();
  const redirectTo = location.state?.redirectTo || '/app/book';

  const [venue, setVenue] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [failed, setFailed] = useState(false);
  const [photo, setPhoto] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setVenue(null);
    setFailed(false);
    setPhoto(0);
    api.get(`/academies/${venueId}`)
      .then(({ data }) => { if (!cancelled) setVenue(data); })
      .catch(() => { if (!cancelled) setFailed(true); });
    api.get(`/academies/${venueId}/reviews`, { params: { limit: 3 } })
      .then(({ data }) => { if (!cancelled) setReviews(data.reviews || []); })
      .catch(() => {});
    api.get('/offers', { params: { academy_id: venueId } })
      .then(({ data }) => { if (!cancelled) setOffers(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [venueId]);

  const go = (path) => {
    selectVenue(venueId);
    navigate(path);
  };

  if (failed) {
    return (
      <div className="py-16 text-center space-y-3" data-testid="venue-details-error">
        <div className="text-white font-medium">This venue isn't available.</div>
        <button type="button" onClick={() => navigate('/app/venues')} className="text-volt text-sm font-semibold hover:underline">
          Back to venues
        </button>
      </div>
    );
  }
  if (!venue) return <div className="text-white/40 text-sm">Loading venue…</div>;

  const photos = (venue.photos?.length ? venue.photos : venue.logo_url ? [venue.logo_url] : []).map(mediaUrl);
  const primaryIsPlans = redirectTo.startsWith('/app/memberships');

  return (
    <div className="space-y-8 pb-24 sm:pb-0" data-testid="venue-details">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-volt"
      >
        <ArrowLeft size={13} /> All venues
      </button>

      {/* Gallery */}
      <div className="relative bg-surface border border-white/10 aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
        {photos.length ? (
          <img src={photos[photo]} alt={venue.name} className="w-full h-full object-cover" data-testid="venue-photo" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin size={40} className="text-volt/40" />
          </div>
        )}
        {photos.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              onClick={() => setPhoto((photo - 1 + photos.length) % photos.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/60 flex items-center justify-center hover:bg-black/80"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              onClick={() => setPhoto((photo + 1) % photos.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/60 flex items-center justify-center hover:bg-black/80"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {photos.map((_, i) => (
                <button
                  type="button"
                  key={i}
                  aria-label={`Photo ${i + 1}`}
                  onClick={() => setPhoto(i)}
                  className={`w-2 h-2 rounded-full ${i === photo ? 'bg-volt' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Header */}
          <div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">{venue.name}</h1>
            {(venue.address || venue.city || venue.state) && (
              <div className="text-sm text-white/50 mt-2 flex items-start gap-1.5">
                <MapPin size={14} className="mt-0.5 shrink-0" /> {[venue.address, venue.city, venue.state, venue.pincode].filter(Boolean).join(', ')}
              </div>
            )}
            <div className="flex items-center gap-2 mt-3" data-testid="venue-rating">
              {venue.review_count > 0 ? (
                <>
                  <StarRating value={venue.average_rating} size={15} />
                  <span className="text-sm text-white/70">{venue.average_rating}</span>
                  <span className="text-xs text-white/40">({venue.review_count} review{venue.review_count === 1 ? '' : 's'})</span>
                </>
              ) : (
                <span className="text-xs text-white/40">No reviews yet</span>
              )}
            </div>
          </div>

          {/* Offers */}
          {offers.length > 0 && (
            <div data-testid="venue-offers">
              <SectionTitle>Offers</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-3">
                {offers.map((o) => (
                  <div key={o.id} className="border border-volt/40 bg-volt/5 p-4 flex gap-3">
                    <Tag size={16} className="text-volt shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">{o.title}</div>
                      <div className="text-xs text-volt mt-0.5">{offerValue(o)} {offerScope[o.applies_to] || ''}</div>
                      {o.description && <div className="text-xs text-white/50 mt-1">{o.description}</div>}
                      {o.end_date && <div className="text-[10px] uppercase tracking-[0.15em] text-white/30 mt-1">Until {o.end_date}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-white/40 mt-2">Applied automatically at checkout.</p>
            </div>
          )}

          {/* About */}
          {venue.about && (
            <div>
              <SectionTitle>About the venue</SectionTitle>
              <p className="text-white/70 leading-relaxed whitespace-pre-line">{venue.about}</p>
            </div>
          )}

          {/* Facilities */}
          <div data-testid="venue-facilities">
            <SectionTitle>Courts &amp; sports</SectionTitle>
            {venue.facilities?.length ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {venue.facilities.map((f) => (
                  <div key={f.sport_id || 'courts'} className="bg-surface border border-white/10 p-4">
                    <div className="font-heading font-bold uppercase">{f.sport_name}</div>
                    <div className="text-sm text-white/60 mt-1">
                      {f.court_count} court{f.court_count === 1 ? '' : 's'}
                      {f.min_hourly_rate != null && <> · from ₹{f.min_hourly_rate}/hr</>}
                    </div>
                    <div className="text-xs text-white/40 mt-1 flex items-center gap-1">
                      <Clock size={11} /> {formatHour(f.open_hour)} – {formatHour(f.close_hour)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-white/40">No courts are open for booking yet.</div>
            )}
          </div>

          {/* Amenities */}
          {venue.amenities?.length > 0 && (
            <div data-testid="venue-amenities">
              <SectionTitle>Facilities &amp; amenities</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {venue.amenities.map((key) => {
                  const a = amenityInfo(key);
                  const Icon = a.icon || CheckCircle2;
                  return (
                    <span key={key} className="flex items-center gap-1.5 border border-white/10 bg-surface px-3 py-2 text-sm text-white/80">
                      <Icon size={14} className="text-volt" /> {a.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reviews */}
          {reviews.length > 0 && (
            <div>
              <SectionTitle>What players say</SectionTitle>
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-surface border border-white/10 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold">{r.user_name || 'Player'}</span>
                      <StarRating value={r.rating} size={12} />
                    </div>
                    {r.comment && <p className="text-sm text-white/60 mt-2">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <aside className="space-y-4">
          <div className="bg-surface border border-white/10 p-6 space-y-3 lg:sticky lg:top-24">
            <Button
              className="w-full"
              onClick={() => go(primaryIsPlans ? '/app/memberships' : '/app/book')}
              data-testid="venue-primary-cta"
            >
              {primaryIsPlans ? <><BadgeCheck size={16} /> See plans</> : <>Book a court <ArrowRight size={16} /></>}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => go(primaryIsPlans ? '/app/book' : '/app/memberships')}
              data-testid="venue-secondary-cta"
            >
              {primaryIsPlans ? 'Book a court' : 'Membership plans'}
            </Button>
            {(venue.contact_phone || venue.contact_email) && (
              <div className="pt-3 border-t border-white/10 space-y-2 text-sm text-white/60">
                {venue.contact_phone && (
                  <a href={`tel:${venue.contact_phone}`} className="flex items-center gap-2 hover:text-volt">
                    <Phone size={14} /> {venue.contact_phone}
                  </a>
                )}
                {venue.contact_email && (
                  <a href={`mailto:${venue.contact_email}`} className="flex items-center gap-2 hover:text-volt">
                    <Mail size={14} /> {venue.contact_email}
                  </a>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
